// Phase HUB-DRILL-1 — Main Hubs list (founder/admin/MHM-scoped).
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Search } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import useTimedFetch from "../../hooks/useTimedFetch";
import HubDetailDrawer from "../../components/founder/HubDetailDrawer";
import { HubDetailError } from "../../components/founder/MainHubDetailView";

const ORANGE = "#FF6B00";

function Body() {
  const nav = useNavigate();
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [openHub, setOpenHub] = useState(null);
  const { data, loading, error, reload } = useTimedFetch("/founder/main-hubs",
    { params: { search: search || undefined, city: city || undefined, status: status || undefined } });
  const cities = useMemo(() => [...new Set((data?.rows || []).map((r) => r.city).filter(Boolean))].sort(), [data]);
  const rows = data?.rows || [];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="founder-main-hubs-list">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => nav("/dashboard/ceo")}
                data-testid="founder-back-to-ceo"
                className="text-[#FF6B00] hover:opacity-80">
          <ArrowLeft className="h-5 w-5"/>
        </button>
        <h1 className="text-2xl font-bold text-slate-900">Main Hubs</h1>
        <span className="text-sm text-slate-500">· {rows.length} of {data?.total ?? 0}</span>
        <button onClick={reload} className="ml-auto text-slate-400 hover:text-[#FF6B00]"
                aria-label="Refresh" data-testid="main-hubs-refresh">
          <RefreshCw className="h-4 w-4"/>
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search hub name or ID"
                 data-testid="main-hubs-search"
                 className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:border-[#FF6B00] focus:outline-none"/>
        </div>
        <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="main-hubs-city-filter"
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5">
          <option value="">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="main-hubs-status-filter"
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending_approval">Pending approval</option>
        </select>
      </div>

      {error && <HubDetailError {...error} onRetry={reload}/>}
      {loading && !data && (
        <div className="space-y-2" data-testid="main-hubs-loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse"/>
          ))}
        </div>
      )}

      {data && rows.length === 0 && !loading && (
        <div className="text-center text-slate-400 py-12" data-testid="main-hubs-empty">
          No main hubs match the filters.
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50/70">
              <tr className="text-left text-slate-500">
                <Th>Hub name</Th>
                <Th>ID</Th>
                <Th>City</Th>
                <Th>Manager</Th>
                <Th className="text-right">Micro hubs</Th>
                <Th className="text-right">Orders today</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.hub_id}
                    onClick={() => setOpenHub(r.hub_id)}
                    data-testid={`main-hub-row-${r.hub_id}`}
                    className="hover:bg-orange-50/40 cursor-pointer focus-within:bg-orange-50">
                  <td className="px-3 py-2 font-semibold">
                    <button onClick={(e) => { e.stopPropagation(); setOpenHub(r.hub_id); }}
                            data-testid={`main-hub-name-${r.hub_id}`}
                            className="text-[#C2410C] hover:underline">{r.name || "—"}</button>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.hub_id}</td>
                  <td className="px-3 py-2">{r.city || "—"}</td>
                  <td className="px-3 py-2">{r.manager_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                  <td className="px-3 py-2 text-right">{r.micro_hubs_count}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#FF6B00]">{r.orders_today}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${
                      r.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : r.status === "pending_approval" ? "bg-amber-100 text-amber-700 border-amber-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}>{r.status || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* HUB-DRILL-1 — read-only drill-down surface. Hub-mutation
                        actions (Assign / etc.) stay on the existing CEO
                        Central Operations page at /dashboard/ceo. This pill
                        preserves the visual affordance the user expected
                        from their reference screenshot. */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); nav(`/dashboard/founder/main-hubs/${r.hub_id}`); }}
                      data-testid={`main-hub-view-${r.hub_id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-[#C2410C] border border-orange-200 hover:bg-orange-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]">
                      View <span aria-hidden="true">→</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <HubDetailDrawer hubId={openHub} open={!!openHub} onClose={() => setOpenHub(null)}/>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 font-semibold uppercase tracking-wider text-[10px] ${className}`}>{children}</th>;
}

export default function MainHubsList() {
  return (
    <RoleGate allow={["founder", "admin", "main_hub_manager", "ceo"]}>
      <Body/>
    </RoleGate>
  );
}
